import { Button } from "primereact/button";
import { Card } from "primereact/card";
import { InputText } from "primereact/inputtext";
import { Toast } from "primereact/toast";
import React, { useRef, useState } from "react";
import { Controller, useFieldArray, useForm } from "react-hook-form";
import {
    appInnerCardStyle,
    appSectionCardStyle,
    renderSurfaceHeader,
} from "../../../../components/general/uiSurfaceStyles";
import NurseDateField from "../NurseDateField";
import { normalizeDateInputValue } from "../dateValueUtils";
import { useNurseFormAutoSave } from "../useNurseFormAutoSave";

// Type definitions

export interface DiagnosisEntry {
    description: string;
    conductedBy: string;
    date: string;
}

export interface HistoryEntry {
    text: string;
}

export interface Step04FormValues {
    diagnoses: DiagnosisEntry[];
    recentHistory: HistoryEntry[];
}

export const DEFAULT_STEP04: Step04FormValues = {
    diagnoses: [{ description: "", conductedBy: "", date: "" }],
    recentHistory: [{ text: "" }],
};

// Props

export interface Step04Props {
    sectionData: Partial<Step04FormValues> | null;
    residentId: string;
    formId: string;
    onSave: (data: Step04FormValues) => Promise<void>;
    onNext: () => void;
    onBack: () => void;
    loading?: boolean;
}

const InfoRow: React.FC<{ label: string; value?: string | null }> = ({ label, value }) => (
    <div className="col-12 md:col-4 mb-2">
        <div className="text-xs font-medium mb-1" style={{ color: "#6b7280" }}>{label}</div>
        <div className="text-sm font-medium" style={{ color: value ? "#111827" : "#9ca3af" }}>{value || "—"}</div>
    </div>
);

// Component

const Step04CurrentMedicalDiagnosis: React.FC<Step04Props> = ({
    sectionData, residentId, formId, onSave, onNext, onBack, loading,
}) => {
    const toast = useRef<Toast>(null);
    const [enableEditing, setEnableEditing] = useState<boolean>(false);

    const defaultValues: Step04FormValues = {
        ...DEFAULT_STEP04,
        ...(sectionData ?? {}),
        diagnoses:
            sectionData?.diagnoses && sectionData.diagnoses.length > 0
                ? sectionData.diagnoses.map((d) => ({
                    description: d.description ?? "",
                    conductedBy: d.conductedBy ?? "",
                    date: normalizeDateInputValue(d.date),
                }))
                : [{ description: "", conductedBy: "", date: "" }],
        recentHistory:
            sectionData?.recentHistory && sectionData.recentHistory.length > 0
                ? sectionData.recentHistory.map((h) => ({ text: h.text ?? "" }))
                : [{ text: "" }],
    };

    const { control, handleSubmit, watch, reset } = useForm<Step04FormValues>({ defaultValues });
    const { fields, append, remove } = useFieldArray({ control, name: "diagnoses" });
    const { fields: historyFields, append: appendHistory, remove: removeHistory } = useFieldArray({ control, name: "recentHistory" });

    const stripEmpty = (data: Step04FormValues): Step04FormValues => ({
        ...data,
        diagnoses: data.diagnoses.filter((d) => d.description.trim().length > 0),
        recentHistory: data.recentHistory.filter((h) => h.text.trim().length > 0),
    });

    const onSubmit = async (data: Step04FormValues) => {
        await onSave(stripEmpty(data));
    };

    const handleSave = async (data: Step04FormValues) => {
        try {
            await onSave(stripEmpty(data));
            toast.current?.show({ severity: "success", summary: "Saved", detail: "Medical history saved successfully.", life: 3000 });
        } catch (error: any) {
            toast.current?.show({ severity: "error", summary: "Save failed", detail: error?.message || "An error occurred while saving.", life: 4000 });
        }
    };

    const handleSaveAndContinue = async (data: Step04FormValues) => {
        try {
            await onSave(stripEmpty(data));
            toast.current?.show({ severity: "success", summary: "Saved", detail: "Medical history saved. Moving to next step.", life: 2000 });
            onNext();
        } catch (error: any) {
            toast.current?.show({ severity: "error", summary: "Save failed", detail: error?.message || "An error occurred while saving.", life: 4000 });
        }
    };

    useNurseFormAutoSave({ enabled: enableEditing && !!formId, baselineData: defaultValues, watch, handleSubmit, onSubmit });

    const values = watch();

    return (
        <>
            <Toast ref={toast} />
            <form onSubmit={handleSubmit(handleSave)}>
                <div className="flex justify-content-center gap-2 mb-3">
                    <Button type="button" label="Prev" icon="pi pi-arrow-left" severity="secondary" outlined rounded onClick={onBack} />
                    <Button type="button" label="Edit" icon="pi pi-pencil" severity="warning" outlined rounded visible={!enableEditing} onClick={() => setEnableEditing(true)} />
                    <Button type="button" label="Disable Edit" icon="pi pi-lock" severity="secondary" outlined rounded visible={enableEditing} onClick={() => { setEnableEditing(false); reset(defaultValues); }} />
                    <Button type="submit" label="Save" icon="pi pi-save" severity="warning" outlined rounded loading={loading} visible={enableEditing} />
                    <Button type="button" label="Next" icon="pi pi-arrow-right" iconPos="right" severity="info" outlined rounded onClick={onNext} />
                </div>
                {!enableEditing ? (
                    <>
                        <Card style={appSectionCardStyle} className="mb-3">
                            {renderSurfaceHeader("Current Medical Diagnoses", "Diagnoses and recent medical history.", "pi pi-heart")}
                            <div className="p-3">
                                {(values.diagnoses ?? []).filter(d => d.description).length > 0 ? (
                                    <div className="grid">
                                        {(values.diagnoses ?? []).filter(d => d.description).map((d, i) => (
                                            <React.Fragment key={i}>
                                                <InfoRow label={`Diagnosis ${i + 1}`} value={d.description} />
                                                <InfoRow label="Conducted By" value={d.conductedBy} />
                                                <InfoRow label="Date" value={d.date} />
                                            </React.Fragment>
                                        ))}
                                    </div>
                                ) : <p className="text-sm" style={{ color: "#9ca3af" }}>No diagnoses recorded.</p>}
                                {(values.recentHistory ?? []).filter(h => h.text).length > 0 && (
                                    <>
                                        <div className="font-semibold text-sm mt-3 mb-2">Recent History</div>
                                        {(values.recentHistory ?? []).filter(h => h.text).map((h, i) => (
                                            <div key={i} className="text-sm mb-1" style={{ color: "#374151" }}>{h.text}</div>
                                        ))}
                                    </>
                                )}
                            </div>
                        </Card>
                    </>
                ) : (
                    <>
                        <Card style={appSectionCardStyle} className="mb-3">
                            {renderSurfaceHeader(
                                "Most Recent Exams",
                                "Add one entry per exam. Include who conducted it and the date.",
                                "pi pi-heart",
                            )}
                            <div className="p-3">
                                <div className="flex flex-column gap-3">
                                    {fields.map((field, index) => (
                                        <div key={field.id} style={{ ...appInnerCardStyle, padding: "0.75rem 1rem" }}>
                                            <div className="flex align-items-center justify-content-between mb-2">
                                                <span style={{
                                                    display: "inline-flex", alignItems: "center", justifyContent: "center",
                                                    width: 22, height: 22, borderRadius: "50%",
                                                    background: "#e2e8f0", color: "#475569",
                                                    fontSize: "0.70rem", fontWeight: 700,
                                                }}>
                                                    {index + 1}
                                                </span>
                                                <Button
                                                    type="button"
                                                    icon="pi pi-trash"
                                                    rounded text severity="danger"
                                                    aria-label={`Remove entry ${index + 1}`}
                                                    disabled={fields.length === 1}
                                                    onClick={() => remove(index)}
                                                    style={{ width: 28, height: 28 }}
                                                />
                                            </div>

                                            <Controller
                                                name={`diagnoses.${index}.description`}
                                                control={control}
                                                render={({ field: f }) => (
                                                    <InputText {...f} className="w-full mb-2" placeholder="Diagnosis / condition" />
                                                )}
                                            />

                                            <div style={{ display: "flex", gap: "0.5rem", flexWrap: "wrap" }}>
                                                <Controller
                                                    name={`diagnoses.${index}.conductedBy`}
                                                    control={control}
                                                    render={({ field: f }) => (
                                                        <InputText
                                                            {...f}
                                                            placeholder="Conducted by"
                                                            style={{ flex: "1 1 160px", minWidth: 0 }}
                                                        />
                                                    )}
                                                />
                                                <Controller
                                                    name={`diagnoses.${index}.date`}
                                                    control={control}
                                                    render={({ field: f }) => (
                                                        <NurseDateField
                                                            value={f.value}
                                                            onChange={f.onChange}
                                                            placeholder="Date"
                                                            style={{ flex: "0 0 160px" }}
                                                            inputStyle={{ width: "100%" }}
                                                        />
                                                    )}
                                                />
                                            </div>
                                        </div>
                                    ))}
                                </div>

                                <Button
                                    type="button"
                                    label="Add Entry"
                                    icon="pi pi-plus"
                                    severity="secondary"
                                    outlined rounded className="mt-3"
                                    onClick={() => append({ description: "", conductedBy: "", date: "" })}
                                />
                            </div>
                        </Card>

                        <Card style={appSectionCardStyle} className="mb-3">
                            {renderSurfaceHeader(
                                "Recent Medical History",
                                "List relevant medical history, conditions, or background information.",
                                "pi pi-list",
                            )}
                            <div className="p-3">
                                <div className="flex flex-column gap-2">
                                    {historyFields.map((field, index) => (
                                        <div key={field.id} style={{ display: "flex", gap: "0.5rem", alignItems: "center" }}>
                                            <span style={{
                                                display: "inline-flex", alignItems: "center", justifyContent: "center",
                                                width: 22, height: 22, borderRadius: "50%",
                                                background: "#e2e8f0", color: "#475569",
                                                fontSize: "0.70rem", fontWeight: 700, flexShrink: 0,
                                            }}>
                                                {index + 1}
                                            </span>
                                            <Controller
                                                name={`recentHistory.${index}.text`}
                                                control={control}
                                                render={({ field: f }) => (
                                                    <InputText
                                                        {...f}
                                                        className="flex-1"
                                                        placeholder="Medical history item"
                                                    />
                                                )}
                                            />
                                            <Button
                                                type="button"
                                                icon="pi pi-trash"
                                                rounded text severity="danger"
                                                aria-label={`Remove item ${index + 1}`}
                                                disabled={historyFields.length === 1}
                                                onClick={() => removeHistory(index)}
                                                style={{ width: 28, height: 28, flexShrink: 0 }}
                                            />
                                        </div>
                                    ))}
                                </div>
                                <Button
                                    type="button"
                                    label="Add Item"
                                    icon="pi pi-plus"
                                    severity="secondary"
                                    outlined rounded className="mt-3"
                                    onClick={() => appendHistory({ text: "" })}
                                />
                            </div>
                        </Card>

                    </>
                )}
                <div className="flex justify-content-center gap-2 mt-3">
                    <Button type="button" label="Prev" icon="pi pi-arrow-left" severity="secondary" outlined rounded onClick={onBack} />
                    <Button type="button" label="Edit" icon="pi pi-pencil" severity="warning" outlined rounded visible={!enableEditing} onClick={() => setEnableEditing(true)} />
                    <Button type="button" label="Disable Edit" icon="pi pi-lock" severity="secondary" outlined rounded visible={enableEditing} onClick={() => { setEnableEditing(false); reset(defaultValues); }} />
                    <Button type="submit" label="Save" icon="pi pi-save" severity="warning" outlined rounded loading={loading} visible={enableEditing} />
                    <Button type="button" label="Next" icon="pi pi-arrow-right" iconPos="right" severity="info" outlined rounded onClick={onNext} />
                </div>
            </form>
        </>
    );
};

export default Step04CurrentMedicalDiagnosis;
